/** Sequential Photo Mode: walk unfinished checklist tasks (photos + check-only). */

export type PhotoModeAction = 'take_photo' | 'mark_done'

export function unfinishedTasks<T extends { is_done: boolean }>(tasks: T[]): T[] {
  return tasks.filter((t) => !t.is_done)
}

export function firstUnfinishedTaskId(
  tasks: Array<{ id: string; is_done: boolean }>
): string | null {
  return tasks.find((t) => !t.is_done)?.id ?? null
}

/**
 * Next unfinished task after `currentId`, wrapping to the start.
 * If `currentId` is still unfinished, it is only returned when it is the last remaining item.
 */
export function nextUnfinishedTaskId(
  tasks: Array<{ id: string; is_done: boolean }>,
  currentId: string | null
): string | null {
  const remaining = tasks.filter((t) => !t.is_done)
  if (remaining.length === 0) return null
  if (!currentId) return remaining[0].id
  const idx = tasks.findIndex((t) => t.id === currentId)
  const start = idx < 0 ? 0 : idx + 1
  for (let i = 0; i < tasks.length; i++) {
    const t = tasks[(start + i) % tasks.length]
    if (!t.is_done) return t.id
  }
  return null
}

export function taskNeedsPhoto(
  task: { photo_required?: boolean },
  photoCount: number
): boolean {
  return !!task.photo_required && photoCount === 0
}

export function photoModeActionForTask(
  task: { photo_required?: boolean },
  photoCount: number
): PhotoModeAction {
  return taskNeedsPhoto(task, photoCount) ? 'take_photo' : 'mark_done'
}

/** Frozen session total from mode entry; remaining drops as items are finished. */
export function photoModeProgress(
  sessionTotal: number,
  remainingCount: number
): { current: number; total: number; complete: boolean } {
  const total = Math.max(0, sessionTotal)
  if (total === 0 || remainingCount <= 0) {
    return { current: total, total, complete: true }
  }
  const completed = Math.max(0, total - remainingCount)
  return {
    current: Math.min(completed + 1, total),
    total,
    complete: false,
  }
}
